import { z } from 'zod';

/**
 * Two distinct schemas — same reasoning as EquipmentType:
 *   - `tag`, `equipmentTypeId`, `vesselId` are immutable after
 *     creation (the backend rejects mutations on them to preserve
 *     identity and FK integrity).
 *   - `parentEquipmentId` is mutable in update mode (reparenting).
 *
 *   equipmentCreateSchema  full payload, used by the create form.
 *   equipmentUpdateSchema  editable fields only.
 *
 * Dates flow from the form as `YYYY-MM-DD` strings (HTML <input
 * type="date">). They're sent as-is to the backend, which uses
 * `class-transformer @Type(() => Date)` to coerce them to `Date`.
 */

const optionalString = (max: number) =>
  z
    .string()
    .max(max)
    .optional()
    .transform((v) => (v === undefined || v === '' ? null : v));

/** ISO date `YYYY-MM-DD` or null. */
const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
  .or(z.literal(''))
  .optional()
  .transform((v) => (v === undefined || v === '' ? null : v));

const optionalUuid = z
  .string()
  .uuid()
  .or(z.literal(''))
  .optional()
  .transform((v) => (v === undefined || v === '' ? null : v));

/** Fields editable in BOTH create and update modes. */
const editableFields = {
  name: z.string().min(1).max(200),
  serialNumber: optionalString(100),
  parentEquipmentId: optionalUuid,
  location: optionalString(200),
  installedAt: optionalDate,
  commissionedAt: optionalDate,
  warrantyEndDate: optionalDate,
  criticality: z.coerce.number().int().min(1).max(5).default(3),
};

export const equipmentCreateSchema = z.object({
  ...editableFields,
  tag: z
    .string()
    .min(2)
    .max(50)
    .regex(/^[A-Z0-9_-]+$/, 'Tag: uppercase letters, digits, _ or - only'),
  equipmentTypeId: z.string().uuid(),
  vesselId: z.string().uuid(),
  isActive: z.boolean().default(true),
});

export type EquipmentCreateValues = z.infer<typeof equipmentCreateSchema>;

export const equipmentUpdateSchema = z.object({
  ...editableFields,
  isActive: z.boolean().optional(),
});

export type EquipmentUpdateValues = z.infer<typeof equipmentUpdateSchema>;
